import React, {useEffect, useMemo, useState} from 'react';
import {Link} from "react-router-dom";

import './student_intro_v2.scss';

import Students from "./Students";
import SceneProps from "./SceneProps";
import {units, STAGE_HEIGHT, STAGE_WIDTH} from "./stage";

const SCENE_SECONDS = 5.5;

const SCENES = [
    {
        key: 'hello',
        title: 'Hi! Welcome to Mindsets',
        text: 'We’re so glad you’re here.'
    },
    {
        key: 'what',
        title: 'Math for the real world',
        text: 'Design, cook, build and create, all with math.'
    },
    {
        key: 'code',
        step: 1,
        title: 'Get a code from your teacher',
        text: 'It’s your ticket in.'
    },
    {
        key: 'challenge',
        step: 2,
        title: 'Dive into a challenge',
        text: 'Try ideas. Mistakes are clues!'
    },
    {
        key: 'share',
        step: 3,
        title: 'Show your thinking',
        text: 'There’s more than one way to get there.'
    },
    {
        key: 'together',
        title: 'You belong here!',
        text: 'Ready when you are.'
    }
];

const CONFETTI_COLOURS = ['#FFC84D', '#ef5484', '#6db7a0', '#4286F4', '#ffffff', '#FF6D02'];

function Confetti() {
    const pieces = useMemo(() => Array.from({length: 48}, (value, index) => ({
        left: Math.random() * 100,
        colour: CONFETTI_COLOURS[index % CONFETTI_COLOURS.length],
        delay: Math.random() * 1.5,
        duration: 2.5 + Math.random() * 2,
        drift: (Math.random() - 0.5) * 160,
        round: index % 3 === 0
    })), []);

    return (
        <div className='v2-confetti' aria-hidden='true'>
            {pieces.map((piece, index) => (
                <span
                    key={index}
                    className={'v2-confetti-piece' + (piece.round ? ' round' : '')}
                    style={{
                        left: `${piece.left}%`,
                        backgroundColor: piece.colour,
                        animationDelay: `${piece.delay}s`,
                        animationDuration: `${piece.duration}s`,
                        '--drift': `${piece.drift}px`
                    }}
                />
            ))}
        </div>
    );
}

export default function () {
    const [index, setIndex] = useState(0);
    const [playing, setPlaying] = useState(true);
    const [playId, setPlayId] = useState(0);

    const scene = SCENES[index];
    const isLast = index === SCENES.length - 1;

    function goTo(target) {
        setIndex(Math.max(0, Math.min(SCENES.length - 1, target)));
    }

    function replay() {
        setIndex(0);
        setPlayId(playId + 1);
        setPlaying(true);
    }

    function onProgressEnd(event) {
        if (event.target === event.currentTarget && !isLast) {
            goTo(index + 1);
        }
    }

    useEffect(() => {
        const previousTitle = document.title;
        document.title = 'Welcome to Mindsets!';

        return () => {
            document.title = previousTitle;
        }
    }, []);

    useEffect(() => {
        function onKeyDown(event) {
            if (event.key === 'ArrowRight') {
                goTo(index + 1);
            } else if (event.key === 'ArrowLeft') {
                goTo(index - 1);
            }
        }
        window.addEventListener('keydown', onKeyDown);

        return () => window.removeEventListener('keydown', onKeyDown);
    }, [index]);

    return (
        <div
            id='student-intro-v2'
            data-scene={scene.key}
            className={playing ? '' : 'is-paused'}
        >
            <div className='v2-decor' aria-hidden='true'>
                <span className='v2-cloud cloud-0'/>
                <span className='v2-cloud cloud-1'/>
                <span className='v2-cloud cloud-2'/>
            </div>

            <div className='v2-top'>
                <div className='v2-progress'>
                    {SCENES.map((item, itemIndex) => (
                        <button
                            key={item.key}
                            className='v2-progress-segment'
                            onClick={() => goTo(itemIndex)}
                            aria-label={`Go to part ${itemIndex + 1}: ${item.title}`}
                            aria-current={itemIndex === index ? 'step' : undefined}
                        >
                            <span className='v2-progress-track'>
                                <span
                                    key={itemIndex === index ? `${playId}-${index}` : itemIndex}
                                    className={
                                        'v2-progress-fill' +
                                        (itemIndex < index ? ' is-done' : '') +
                                        (itemIndex === index ? ' is-active' : '')
                                    }
                                    style={{animationDuration: `${SCENE_SECONDS}s`}}
                                    onAnimationEnd={itemIndex === index ? onProgressEnd : undefined}
                                />
                            </span>
                        </button>
                    ))}
                </div>
                <div className='v2-top-row'>
                    <img
                        className='v2-logo'
                        src={`${process.env.PUBLIC_S3_ASSETS_URL}/logos/mindsets_logo_white.svg`}
                        alt='Mindsets'
                    />
                    {!isLast ? (
                        <button className='v2-skip' onClick={() => goTo(SCENES.length - 1)}>Skip</button>
                    ) : null}
                </div>
            </div>

            <div className='v2-copy' key={`${playId}-${index}`} aria-live='polite'>
                {scene.step ? <div className='v2-step'>Step {scene.step} of 3</div> : null}
                <h1 className='v2-title'>{scene.title}</h1>
                <p className='v2-text'>{scene.text}</p>
            </div>

            <div
                className='v2-stage'
                style={{width: units(STAGE_WIDTH), height: units(STAGE_HEIGHT)}}
            >
                <Students scene={scene.key} playId={playId}/>
                <SceneProps key={`${playId}-${index}`} scene={scene.key}/>
            </div>

            <div className='v2-ground'>
                <div className='v2-controls'>
                    <button
                        className='v2-control'
                        onClick={() => goTo(index - 1)}
                        disabled={index === 0}
                        aria-label='Back'
                    >
                        ◀
                    </button>
                    {isLast ? (
                        <>
                            <Link className='v2-go' to='/login'>Let’s go!</Link>
                            <button className='v2-control' onClick={replay} aria-label='Watch again'>↺</button>
                        </>
                    ) : (
                        <>
                            <button
                                className='v2-control v2-control-play'
                                onClick={() => setPlaying(!playing)}
                                aria-label={playing ? 'Pause' : 'Play'}
                            >
                                {playing ? '❚❚' : '▶'}
                            </button>
                            <button className='v2-control' onClick={() => goTo(index + 1)} aria-label='Next'>
                                ▶
                            </button>
                        </>
                    )}
                </div>
            </div>

            {isLast ? <Confetti key={playId}/> : null}
        </div>
    );
}
